import React from "react";
import PropTypes from "prop-types";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import annotateViewStateToProps from "../selectors";
import * as actions from "../actions";
import AnnotateLayout from "./AnnotateLayout";

const formShape = PropTypes.shape({
  collected: PropTypes.string,
  targetDepthM: PropTypes.number,
  siteId: PropTypes.string,
  drop: PropTypes.number
});

const castShape = PropTypes.shape({
  soloCastPk: PropTypes.number,
  startDt: PropTypes.string,
  endDt: PropTypes.string,
  miscast: PropTypes.bool,
  comments: PropTypes.string
});

const fileShape = PropTypes.shape({
  filename: PropTypes.string.isRequired,
  soloFilePk: PropTypes.number.isRequired,
  numCasts: PropTypes.number
});

class AnnotateView extends React.Component {
  static propTypes = {
    workarea: PropTypes.string,
    organization: PropTypes.string,
    possibleForms: PropTypes.objectOf(PropTypes.arrayOf(formShape)),
    soloFilesBeingAnnotated: PropTypes.arrayOf(PropTypes.number),
    soloFilesList: PropTypes.arrayOf(fileShape).isRequired,
    soloAnnotationModalCast: castShape,
    soloChartModalCast: castShape,
    soloAnnotationModalForms: PropTypes.arrayOf(formShape),
    soloAnnotationModalSelected: PropTypes.number,
    actions: PropTypes.shape({
      soloFilesToAnnotateInvalidate: PropTypes.func.isRequired,
      soloFilesToAnnotateFetchIfNeeded: PropTypes.func.isRequired,
      soloUpdateFile: PropTypes.func.isRequired,
      soloRemoveFile: PropTypes.func.isRequired,
      soloCastsToAnnotateFetchIfNeeded: PropTypes.func.isRequired,
      soloSetMatchedForm: PropTypes.func.isRequired,
      soloUpdateCast: PropTypes.func.isRequired,
      soloAnnotateFiles: PropTypes.func.isRequired,
      soloOpenAnnotationModal: PropTypes.func.isRequired,
      soloCloseAnnotationModal: PropTypes.func.isRequired,
      soloSetAnnotationModalCast: PropTypes.func.isRequired,
      soloSetAnnotationModalForms: PropTypes.func.isRequired,
      soloSetAnnotationModalSelected: PropTypes.func.isRequired,
      soloAddFileToAnnotate: PropTypes.func.isRequired,
      soloRemoveFileToAnnotate: PropTypes.func.isRequired,
      soloCloseChartModal: PropTypes.func.isRequired,
      soloOpenChartModal: PropTypes.func.isRequired,
      soloSetChartModalCast: PropTypes.func.isRequired,
      soloUpdateChartModalCast: PropTypes.func.isRequired,
      soloChartDataFetch: PropTypes.func.isRequired,
      soloCloseAnnotationSnackbar: PropTypes.func.isRequired
    }).isRequired
  };

  static defaultProps = {
    soloFilesBeingAnnotated: [],
    possibleForms: {},
    soloAnnotationModalForms: [],
    soloAnnotationModalCast: {},
    soloChartModalCast: {},
    workarea: null,
    organization: null,
    soloAnnotationModalSelected: null
  };

  componentDidMount() {
    document.title = "Solo Annotate";
    this.props.actions.soloFilesToAnnotateFetchIfNeeded();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.workarea !== this.props.workarea)
      this.props.actions.soloFilesToAnnotateFetchIfNeeded();
  }

  handleAnnotationModalClose() {
    this.props.actions.soloCloseAnnotationModal();
  }

  handleAnnotationRowSelect(rowIndex) {
    this.props.actions.soloSetAnnotationModalSelected(rowIndex);
  }

  handleAnnotationUpdateClick() {
    const { soloCastPk } = this.props.soloAnnotationModalCast;
    const soloMatchedForm = this.props.soloAnnotationModalForms[
      this.props.soloAnnotationModalSelected
    ];
    this.props.actions.soloSetMatchedForm({ soloCastPk, soloMatchedForm });
    this.props.actions.soloCloseAnnotationModal();
  }

  handleFilesTableRowSelect(rowIndex) {
    const { soloFilePk } = this.props.soloFilesList[rowIndex];
    if (this.props.soloFilesBeingAnnotated.includes(soloFilePk)) {
      this.props.actions.soloRemoveFileToAnnotate({ soloFilePk });
    } else {
      this.props.actions.soloAddFileToAnnotate({ soloFilePk });
      this.props.actions.soloCastsToAnnotateFetchIfNeeded({ soloFilePk });
    }
  }

  handleFilesRefreshClick() {
    this.props.actions.soloFilesToAnnotateInvalidate();
    this.props.actions.soloFilesToAnnotateFetchIfNeeded();
  }

  handleFileCommentsChange(soloFilePk, comment) {
    const update = { comments: comment };
    this.props.actions.soloUpdateFile({ soloFilePk, update });
  }

  handleRemoveFileClick(soloFilePk) {
    this.props.actions.soloRemoveFile(soloFilePk);
  }

  handleCastChangeFormClick(cast) {
    const forms = this.props.possibleForms[cast.soloCastPk];
    this.props.actions.soloSetAnnotationModalCast(cast);
    this.props.actions.soloSetAnnotationModalForms(forms);
    this.props.actions.soloSetAnnotationModalSelected(undefined);
    this.props.actions.soloOpenAnnotationModal();
  }

  handleCastMiscastCheck(soloCastPk, soloFilePk, checked) {
    const update = { miscast: checked };
    this.props.actions.soloUpdateCast({ soloCastPk, soloFilePk, update });
  }

  handleCastCommentsChange(soloCastPk, soloFilePk, comment) {
    const update = { comments: comment };
    this.props.actions.soloUpdateCast({ soloCastPk, soloFilePk, update });
  }

  handleAnnotateFileClick() {
    this.props.actions.soloAnnotateFiles();
  }

  handleChartModalClose() {
    this.props.actions.soloCloseChartModal();
  }

  handleCreateChart(cast) {
    this.props.actions.soloSetChartModalCast(cast);
    this.props.actions.soloChartDataFetch(cast);
    this.props.actions.soloOpenChartModal();
  }

  handleSoloAnnotationSnackbarClose() {
    this.props.actions.soloCloseAnnotationSnackbar();
  }

  handleSoloChartChangeBottomDt({
    modalBottomDatetime,
    modalBottomDepth,
    soloCastPk,
    soloFilePk
  }) {
    const update = {
      bottomDepth: modalBottomDepth,
      bottomDt: modalBottomDatetime
    };
    this.props.actions.soloUpdateCast({ soloCastPk, soloFilePk, update });
    this.props.actions.soloUpdateChartModalCast(update);
    this.props.actions.soloCloseChartModal();
  }

  render() {
    return (
      <AnnotateLayout
        {...this.props}
        onAnnotationModalClose={() => this.handleAnnotationModalClose()}
        onAnnotationRowSelect={(e, rowIndex) =>
          this.handleAnnotationRowSelect(rowIndex)
        }
        onAnnotationUpdateClick={() => this.handleAnnotationUpdateClick()}
        onFilesTableRowSelect={(e, rowIndex) =>
          this.handleFilesTableRowSelect(rowIndex)
        }
        onFilesRefreshClick={() => this.handleFilesRefreshClick()}
        onFileCommentsChange={(soloFilePk, comment) =>
          this.handleFileCommentsChange(soloFilePk, comment)
        }
        onRemoveFileClick={soloFilePk => this.handleRemoveFileClick(soloFilePk)}
        onCastMiscastCheck={(soloCastPk, soloFilePk, checked) =>
          this.handleCastMiscastCheck(soloCastPk, soloFilePk, checked)
        }
        onCastCommentsChange={(soloCastPk, soloFilePk, comment) =>
          this.handleCastCommentsChange(soloCastPk, soloFilePk, comment)
        }
        onCastChangeFormClick={cast => this.handleCastChangeFormClick(cast)}
        onAnnotateFileClick={() => this.handleAnnotateFileClick()}
        onChartModalClose={() => this.handleChartModalClose()}
        onCreateChart={cast => this.handleCreateChart(cast)}
        onChangeChartDepthBounds={payload =>
          this.handleSoloChartChangeBottomDt(payload)
        }
        onSoloAnnotationSnackbarClose={() =>
          this.handleSoloAnnotationSnackbarClose()
        }
      />
    );
  }
}

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(actions, dispatch)
});

export default withRouter(
  connect(annotateViewStateToProps, mapDispatchToProps)(AnnotateView)
);
